import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "cs_token";
export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (t) => localStorage.setItem(TOKEN_KEY, t);
export const clearToken = () => localStorage.removeItem(TOKEN_KEY);

const client = axios.create({ baseURL: API });

client.interceptors.request.use((config) => {
  const t = getToken();
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

client.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err.response?.status === 401 && !err.config?.url?.includes("/auth/")) {
      clearToken();
      if (window.location.pathname !== "/login") window.location.href = "/login";
    }
    return Promise.reject(err);
  }
);

export function formatApiError(detail) {
  if (detail == null) return "Bir hata oluştu, tekrar deneyin.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// auth
export const authRegister = (payload) =>
  client.post("/auth/register", payload).then((r) => r.data);
export const authLogin = (email, password) =>
  client.post("/auth/login", { email, password }).then((r) => r.data);
export const authVerify2fa = (pending_token, code) =>
  client.post("/auth/2fa/verify", { pending_token, code }).then((r) => r.data);
export const authSetupVerify = (pending_token, code) =>
  client.post("/auth/2fa/setup-verify", { pending_token, code }).then((r) => r.data);
export const authMe = () => client.get("/auth/me").then((r) => r.data);
export const authLogout = () => client.post("/auth/logout").then((r) => r.data);
export const forgotPassword = (email) =>
  client.post("/auth/forgot-password", { email }).then((r) => r.data);
export const resetPassword = (token, new_password) =>
  client.post("/auth/reset-password", { token, new_password }).then((r) => r.data);

// profile
export const getProfile = () => client.get("/profile").then((r) => r.data);
export const updateProfileName = (name) => client.patch("/profile", { name }).then((r) => r.data);
export const changePassword = (current_password, new_password) =>
  client.post("/profile/change-password", { current_password, new_password }).then((r) => r.data);
export const reset2FA = (current_password) =>
  client.post("/profile/2fa/reset", { current_password }).then((r) => r.data);
export const confirm2FA = (code) => client.post("/profile/2fa/confirm", { code }).then((r) => r.data);

// data
export const getPrices = () => client.get("/prices").then((r) => r.data);
export const getKlines = (symbol, days = "1") =>
  client.get(`/klines/${symbol}`, { params: { days } }).then((r) => r.data);
export const getNews = () => client.get("/news").then((r) => r.data);
export const getSignals = (status) =>
  client.get("/signals", { params: status ? { status } : {} }).then((r) => r.data);
export const getCommentary = () => client.get("/commentary").then((r) => r.data);
export const getPortfolio = () => client.get("/portfolio").then((r) => r.data);
export const getTrades = () => client.get("/trades").then((r) => r.data);
export const runAnalyze = () => client.post("/analyze").then((r) => r.data);
export const approveSignal = (id) => client.post(`/signals/${id}/approve`).then((r) => r.data);
export const rejectSignal = (id) => client.post(`/signals/${id}/reject`).then((r) => r.data);
export const resetPortfolio = () => client.post("/portfolio/reset").then((r) => r.data);

// watchlist
export const getWatchlist = () => client.get("/watchlist").then((r) => r.data);
export const addWatchlist = (item) => client.post("/watchlist", item).then((r) => r.data);
export const removeWatchlist = (symbol) => client.delete(`/watchlist/${symbol}`).then((r) => r.data);
export const searchCoins = (q) => client.get("/coins/search", { params: { q } }).then((r) => r.data);

// auto refresh
export const getAutoRefresh = () => client.get("/settings/auto-refresh").then((r) => r.data);
export const setAutoRefresh = (enabled) =>
  client.post("/settings/auto-refresh", { enabled }).then((r) => r.data);

// bot management
export const getBot = () => client.get("/bot").then((r) => r.data);
export const getBotPlans = () => client.get("/bot/plans").then((r) => r.data);
export const getBotPerformance = () => client.get("/bot/performance").then((r) => r.data);
export const getRiskProfiles = () => client.get("/bot/risk-profiles").then((r) => r.data);
export const getResearchJobs = (kind) => client.get("/research/jobs", { params: { kind } }).then((r) => r.data);
export const getResearchJob = (id) => client.get(`/research/jobs/${id}`).then((r) => r.data);
export const startSimulation = (input) => client.post("/research/simulations", input).then((r) => r.data);
export const startFillCheck = () => client.post("/research/reconciliations").then((r) => r.data);
export const getOpportunities = () => client.get("/opportunities").then((r) => r.data);
export const getFutures = () => client.get("/futures").then((r) => r.data);
export const startBot = (opts) => client.post("/bot/start", opts).then((r) => r.data);
export const stopBot = (botId) => client.post(`/bots/${botId}/stop`).then((r) => r.data);
export const stopAllBots = () => client.post("/bot/stop").then((r) => r.data);

// binance
export const getBinanceStatus = () => client.get("/binance/status").then((r) => r.data);
export const connectBinance = (payload) => client.post("/binance/connect", payload).then((r) => r.data);
export const disconnectBinance = () => client.post("/binance/disconnect").then((r) => r.data);
export const getBinanceBalance = () => client.get("/binance/balance").then((r) => r.data);

// gate.io (LIVE spot trading)
export const getGateStatus = () => client.get("/gate/status").then((r) => r.data);
export const getGateFutures = () => client.get("/gate/futures").then((r) => r.data);
export const connectGate = (payload) => client.post("/gate/connect", payload).then((r) => r.data);
export const disconnectGate = () => client.post("/gate/disconnect").then((r) => r.data);
export const getGateBalance = () => client.get("/gate/balance").then((r) => r.data);
export const placeGateOrder = (payload) => client.post("/gate/order", payload).then((r) => r.data);
export const getGateOrders = (pair) => client.get("/gate/orders", { params: { pair } }).then((r) => r.data);
export const cancelGateOrder = (orderId, pair) => client.delete(`/gate/orders/${orderId}`, { params: { pair } }).then((r) => r.data);
export const getGateTrades = () => client.get("/gate/trades").then((r) => r.data);
export const reconcileGate = () => client.post("/gate/reconcile").then((r) => r.data);
export const getExecutionGuards = () => client.get("/gate/execution-guards").then((r) => r.data);
export const verifyExecutionGuard = (market, symbol) => client.get(`/gate/execution-guards/${encodeURIComponent(market)}/${encodeURIComponent(symbol)}/verify`).then((r) => r.data);

// admin
export const getAdminUsers = () => client.get("/admin/users").then((r) => r.data);
export const getAdminStats = () => client.get("/admin/stats").then((r) => r.data);
export const approveUser = (id) => client.post(`/admin/users/${id}/approve`).then((r) => r.data);
export const rejectUser = (id) => client.post(`/admin/users/${id}/reject`).then((r) => r.data);
export const deleteUser = (id) => client.delete(`/admin/users/${id}`).then((r) => r.data);
export const suspendUser = (id) => client.post(`/admin/users/${id}/suspend`).then((r) => r.data);

// formatters
export const fmtPrice = (n) => n == null || !Number.isFinite(Number(n)) ? "—" : new Intl.NumberFormat("tr-TR", {
  style: "currency", currency: "USD", maximumSignificantDigits: 12,
}).format(Number(n));
export const fmtUsd = (n, max = 2) => {
  if (n === null || n === undefined || isNaN(n)) return "--";
  const abs = Math.abs(n);
  const digits = abs < 1 ? 4 : max;
  return "$" + Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: digits });
};
export const fmtNum = (n, d = 2) => {
  if (n === null || n === undefined || isNaN(n)) return "--";
  return Number(n).toLocaleString("en-US", { maximumFractionDigits: d, minimumFractionDigits: d });
};
